import { type InvoiceType, invoiceTypeEnum } from '@billing/validation/invoice';
import { Claim, CreateDebit, Debit, License, Tariff } from 'interfaces';
import { compareDateDesc, nowISO } from 'utils/dateUtils';

export type DebitGenerationStrategy = 'single' | 'perTariff';
export type GenerationMode = InvoiceType | 'wcb';

export interface DebitGenerationConfig {
  strategy: DebitGenerationStrategy;
  invoiceAmount?: string | null;
  tariffs?: Tariff[];
  fallbackTariffID?: string;
}

export interface InvoiceDebitChanges {
  create: CreateDebit[];
  update: Array<Pick<Debit, 'id' | 'requestedAmount'>>;
}

export const getWCBBillingLicense = ({
  licenses,
  siteID,
  claimPayorID,
}: {
  licenses: License[];
  siteID: string;
  claimPayorID?: string;
}): License | undefined =>
  licenses.find(
    (license) =>
      license.siteID === siteID &&
      (license.payorID === claimPayorID || license.payorName === 'WCB')
  );

export const createUpdatedWCBLicense = ({
  claimPayorID,
  wcbPayorID,
  nextWCBLicense,
  siteID,
}: {
  claimPayorID?: string;
  wcbPayorID?: string;
  nextWCBLicense: string;
  siteID: string;
}): Omit<License, 'payor'> => ({
  payorID: claimPayorID || wcbPayorID || '',
  payorName: 'WCB',
  license: nextWCBLicense,
  siteID,
});

const isMatchingWCBLicense = ({
  license,
  siteID,
  claimPayorID,
  wcbPayorID,
}: {
  license: Pick<License, 'siteID' | 'payorID' | 'payorName'>;
  siteID: string;
  claimPayorID?: string;
  wcbPayorID?: string;
}) =>
  license.siteID === siteID &&
  (license.payorID === claimPayorID ||
    license.payorID === wcbPayorID ||
    license.payorName === 'WCB');

export const upsertWCBLicense = ({
  licenses,
  updatedLicense,
  siteID,
  claimPayorID,
  wcbPayorID,
}: {
  licenses: Array<Omit<License, 'payor'>>;
  updatedLicense: Omit<License, 'payor'>;
  siteID: string;
  claimPayorID?: string;
  wcbPayorID?: string;
}): Array<Omit<License, 'payor'>> => {
  let replaced = false;

  const nextLicenses = licenses.reduce<Array<Omit<License, 'payor'>>>(
    (acc, license) => {
      const shouldReplace = isMatchingWCBLicense({
        license,
        siteID,
        claimPayorID,
        wcbPayorID,
      });

      if (!shouldReplace) {
        acc.push(license);
        return acc;
      }

      if (!replaced) {
        acc.push({
          ...license,
          ...updatedLicense,
        });
        replaced = true;
      }

      return acc;
    },
    []
  );

  if (!replaced) {
    nextLicenses.push(updatedLicense);
  }

  return nextLicenses;
};

export const getDebitGenerationConfig = ({
  isWCBMode,
  invoiceType,
  selectedTariffs,
  invoiceRate,
  fallbackTariffID = '',
}: {
  isWCBMode: boolean;
  invoiceType: InvoiceType;
  selectedTariffs: Tariff[];
  invoiceRate: string | null;
  fallbackTariffID?: string;
}): DebitGenerationConfig => {
  if (isWCBMode || invoiceType === invoiceTypeEnum.tariff) {
    return {
      strategy: 'perTariff',
      tariffs: selectedTariffs,
    };
  }

  return {
    strategy: 'single',
    invoiceAmount: invoiceRate,
    fallbackTariffID,
  };
};

const buildCreateDebit = ({
  claim,
  requestedAmount,
  tariffID,
}: {
  claim: Claim;
  requestedAmount: string;
  tariffID: string;
}): CreateDebit => ({
  claimID: claim.id,
  paidAmount: '0',
  payorID: claim.payorID,
  providerID: claim.providerID,
  requestedAmount,
  adjustment: null,
  status: 'processed',
  type: 'PAYMENT',
  source: 'MANUAL',
  microfilm: '',
  tariffID,
  createdAt: nowISO(),
});

const findLatestDebit = (
  debits: Debit[],
  isEligible: (debit: Debit) => boolean
): Debit | undefined =>
  debits
    .filter(isEligible)
    .sort((left, right) => compareDateDesc(left.createdAt, right.createdAt))[0];

const amountsMatch = (
  currentAmount: string | null,
  requestedAmount: string
): boolean => {
  if (currentAmount === null || currentAmount.trim() === '') {
    return false;
  }

  const current = Number(currentAmount);
  const requested = Number(requestedAmount);

  return Number.isFinite(current) && current === requested;
};

export const buildInvoiceDebitChanges = ({
  claim,
  config,
}: {
  claim: Claim;
  config: DebitGenerationConfig;
}): InvoiceDebitChanges => {
  const {
    strategy,
    invoiceAmount,
    tariffs: debitTariffs = [],
    fallbackTariffID = '',
  } = config;
  const debits = claim.debits || [];
  const changes: InvoiceDebitChanges = { create: [], update: [] };
  const matchesClaimAndPayor = (debit: Debit) =>
    debit.claimID === claim.id && debit.payorID === claim.payorID;

  const reconcileDebit = ({
    tariffID,
    requestedAmount,
    isEligible,
  }: {
    tariffID: string;
    requestedAmount: string;
    isEligible: (debit: Debit) => boolean;
  }) => {
    const existingDebit = findLatestDebit(debits, isEligible);

    if (!existingDebit) {
      changes.create.push(
        buildCreateDebit({ claim, requestedAmount, tariffID })
      );
      return;
    }

    if (!amountsMatch(existingDebit.requestedAmount, requestedAmount)) {
      changes.update.push({ id: existingDebit.id, requestedAmount });
    }
  };

  if (strategy === 'perTariff') {
    debitTariffs.forEach((tariff) => {
      const requestedAmount = Number(tariff.total).toFixed(2);

      reconcileDebit({
        tariffID: tariff.id,
        requestedAmount,
        isEligible: (debit) =>
          matchesClaimAndPayor(debit) &&
          debit.tariffID === tariff.id &&
          debit.type === 'PAYMENT' &&
          (debit.source === 'MANUAL' || debit.source === 'REMITTANCE'),
      });
    });

    return changes;
  }

  reconcileDebit({
    tariffID: fallbackTariffID,
    requestedAmount: parseFloat(invoiceAmount || '0').toFixed(2),
    isEligible: (debit) =>
      matchesClaimAndPayor(debit) &&
      debit.tariffID === fallbackTariffID &&
      debit.type === 'PAYMENT' &&
      debit.source === 'MANUAL',
  });

  return changes;
};
